import React, { useState, useEffect } from "react";
import { Box, RadioGroup, Grid, Button, Typography, FormControlLabel, Radio, CircularProgress } from "@mui/material";
import CardSelect from "bundles/Internal/components/CardSelect";
import stepstoneMappings from "bundles/common/utils/mappings/stepstone";
import sh from "bundles/common/utils/sh";

const Stepstone = ({ jobOffer, values, setValues, handleNextStep, goPreviousStep }) => {
  const [errors, setErrors] = useState([]);
  const [onStepstone, setOnStepstone] = useState(values?.on_stepstone);
  const [isLoading, setIsLoading] = useState(true);
  const [authorization, setAuthorization] = useState(null);

  const fields = [
    { fieldName: "account", mapping: stepstoneMappings.ACCOUNTS, label: "Stepstone Account", multiple: false },
    { fieldName: "sectors", mapping: stepstoneMappings.SECTOR_IDS, label: "Sectors (select 3 max)", multiple: true },
    { fieldName: "contract_type", mapping: stepstoneMappings.CONTRACTTYPE_IDS, label: "Contract Type", multiple: false },
    { fieldName: "work_type", mapping: stepstoneMappings.WORKTYPE_IDS, label: "Work Type", multiple: false },
    { fieldName: "experience", mapping: stepstoneMappings.EXPERIENCE_IDS, label: "Experience", multiple: false },
    { fieldName: "situation", mapping: stepstoneMappings.SITUATION_IDS, label: "Situation", multiple: false },
  ];

  const checkAuthorization = () => {
    setIsLoading(true);
    return sh.get("internal/check_stepstone_authorization", {
      params: {
        region: values.region,
        category_id: values.category_id,
      }
    }).then((res) => {
      setTimeout(() => {
        setAuthorization(res.data);
        setIsLoading(false);
      }, 1000);
    });
  };

  const handleCardSelected = (name, value, multiple) => {
    const currentStepstoneData = values.stepstone_data || {};

    if (multiple) {
      const currentValues = currentStepstoneData[name] || [];
      if (currentValues.includes(value)) {
        // Remove value if already selected
        const newValues = currentValues.filter((v) => v !== value);
        setValues({
          ...values,
          stepstone_data: {
            ...currentStepstoneData,
            [name]: newValues,
          },
        });
        validate({ stepstone_data: { [name]: newValues } });
      } else if (currentValues.length < 3) {
        // Add value if limit is not reached
        const newValues = [...currentValues, value];
        setValues({
          ...values,
          stepstone_data: {
            ...currentStepstoneData,
            [name]: newValues,
          },
        });
        validate({ stepstone_data: { [name]: newValues } });
      }
    } else {
      // Single selection for other fields
      setValues({
        ...values,
        stepstone_data: {
          ...currentStepstoneData,
          [name]: value,
        },
      });
      validate({ stepstone_data: { [name]: value } });
    }
  };

  const validateAndGoNext = (e) => {
    e.preventDefault();
    if (!onStepstone || validate(values)) {
      handleNextStep();
    }
  };

  const validate = (fieldValues = {}) => {
    let temp = { ...errors };
    let required = "Required";

    const stepstoneData = fieldValues.stepstone_data || {};

    if ("account" in stepstoneData) {
      temp.account = stepstoneData.account ? "" : required;
    }

    if ("geography" in stepstoneData) {
      temp.geography = stepstoneData.geography ? "" : required;
    }

    if ("sectors" in stepstoneData) {
      temp.sectors = stepstoneData.sectors && stepstoneData.sectors.length > 0 ? "" : required;
    }

    if ("contract_type" in stepstoneData) {
      temp.contract_type = stepstoneData.contract_type ? "" : required;
    }

    if ("work_type" in stepstoneData) {
      temp.work_type = stepstoneData.work_type ? "" : required;
    }

    if ("experience" in stepstoneData) {
      temp.experience = stepstoneData.experience ? "" : required;
    }

    if ("situation" in stepstoneData) {
      temp.situation = stepstoneData.situation ? "" : required;
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  useEffect(() => {
    if (jobOffer.already_on_stepstone || jobOffer.scheduled_on_stepstone) {
      setIsLoading(false);
    } else {
      checkAuthorization();
    }
  }, []);


  return (
    <Box>
      <Grid container spacing={2} component="form" onSubmit={validateAndGoNext}>
        {/* Stepstone Selection */}
        <Grid item xs={12}>
          <Typography style={{ fontSize: "16px", marginBottom: "16px" }}>
            Do you want to push the job offer to Stepstone?
          </Typography>

          {/* Loading indicator */}
          {isLoading && (
            <Box style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
              <CircularProgress size={14} style={{ marginRight: "8px" }} />
              <Typography style={{ fontSize: "14px", color: "#666" }}>
                Checking authorization...
              </Typography>
            </Box>
          )}

          {/* Already on Stepstone info */}
          {!isLoading && jobOffer.already_on_stepstone && (
            <Typography style={{ fontSize: "14px", marginBottom: "8px", color: "#4caf50" }}>
              ✅ This job is already published on Stepstone.
            </Typography>
          )}

          {/* Scheduled for Stepstone info */}
          {!isLoading && jobOffer.scheduled_on_stepstone && onStepstone && (
            <Typography style={{ fontSize: "14px", marginBottom: "8px", color: "#ff9800" }}>
              📅 This job is scheduled to be published on Stepstone. You can modify the Stepstone data.
            </Typography>
          )}

          {/* Credit Information - only show when authorization is loaded and available */}
          {!isLoading && authorization && authorization.stepstone_available && (
            <Typography style={{ fontSize: "14px", marginBottom: "8px", color: "#666" }}>
              Weekly credits: {authorization.used_credits}/{authorization.weekly_credits} used ({authorization.remaining_credits} remaining)
            </Typography>
          )}

          {/* Authorization Message - only show when authorization is loaded */}
          {!isLoading && authorization && authorization.message && (
            <Typography style={{ color: "red", fontSize: "14px", marginBottom: "8px" }}>
              {authorization.message}
            </Typography>
          )}

          <RadioGroup
            row
            value={onStepstone}
            onChange={(e) => {
              if (!isLoading && (authorization?.stepstone_available || jobOffer.scheduled_on_stepstone) && !jobOffer.already_on_stepstone) {
                const value = e.target.value === "true";
                setOnStepstone(value);
                setValues({ ...values, on_stepstone: value });
              }
            }}
            style={{
              marginBottom: "24px",
              opacity: isLoading || (!authorization?.stepstone_available && !jobOffer.scheduled_on_stepstone) || jobOffer.already_on_stepstone ? 0.9 : 1,
              pointerEvents: isLoading || (!authorization?.stepstone_available && !jobOffer.scheduled_on_stepstone) || jobOffer.already_on_stepstone ? "none" : "auto",
            }}
          >
            <FormControlLabel
              style={{ marginRight: "4rem" }}
              value={true}
              control={<Radio />}
              label="Yes, post this job on Stepstone"
              disabled={isLoading || (!authorization?.stepstone_available && !jobOffer.scheduled_on_stepstone) || jobOffer.already_on_stepstone}
            />
            <FormControlLabel
              value={false}
              control={<Radio />}
              label="No, only on the website"
              disabled={isLoading || (!authorization?.stepstone_available && !jobOffer.scheduled_on_stepstone) || jobOffer.already_on_stepstone}
            />
          </RadioGroup>
        </Grid>

        {onStepstone && (
          <>
            {fields.map(({ fieldName, mapping, label, multiple }) => (
              <Grid item xs={12} key={fieldName}>
                <Box>
                  <Typography>{label}:</Typography>
                  <Box
                    style={{
                      opacity: jobOffer.already_on_stepstone ? 0.68 : 1,
                      pointerEvents: jobOffer.already_on_stepstone ? "none" : "auto",
                    }}
                  >
                    <CardSelect
                      fieldName={fieldName}
                      selected={values.stepstone_data?.[fieldName] || (multiple ? [] : null)}
                      multiple={multiple}
                      values={Object.keys(mapping).map((key) => ({
                        code: mapping[key],
                        label: key,
                      }))}
                      onChange={(name, value) => handleCardSelected(name, value, multiple)}
                    />
                  </Box>
                  {errors[fieldName] && <p style={{ color: "#f44336" }}>{errors[fieldName]}</p>}
                </Box>
              </Grid>
            ))}
          </>
        )}

        {/* Navigation Buttons */}
        <Grid item xs={12}>
          <hr style={{ marginTop: "16px", marginBottom: "32px" }} />
          <Box style={{ display: "flex", justifyContent: "space-between" }}>
            <Button variant="squared" color="primaryOutlined" onClick={goPreviousStep}>Back</Button>
            <Button variant="squared" color="primaryContained" type="submit">Next</Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Stepstone;
